import { Account, type IncomeSplitConfig } from '@fruitful/income-split';
import { AnalyticsUserEvent } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai/index';
import React from 'react';
import {
    BodyLargeEmphasized,
    BodySmallEmphasized,
    BodySmallRegular,
    RadioInput,
    ScreenContainer,
    Separator,
    Switch,
    XStack,
    YStack,
} from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { captureMessage, track } from '../../../logger/Logger';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import { incomeSplitAtom } from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';

const Accounts = ({ incomeSplit }: { incomeSplit: IncomeSplitConfig }) => {
    const user = useAuthedUser();

    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);
    const setIncomeSplit = useSetAtom(incomeSplitAtom);

    const { accounts = [] } = incomeSplit;

    return (
        <YStack gap={12}>
            <YStack>
                <BodyLargeEmphasized>
                    Accounts eligible for Income Split
                </BodyLargeEmphasized>
                <BodySmallRegular color="$gray1000">
                    Apply rules to income deposited into these accounts
                </BodySmallRegular>
            </YStack>
            <YStack
                borderRadius={16}
                backgroundColor="$gray200"
                separator={<Separator borderColor="$gray500" />}
            >
                {bankAccountDetails
                    .filter((account) =>
                        isAccountValidAsIncomeSplitSource(user, account),
                    )
                    .map((account, i) => (
                        <Account
                            key={i}
                            account={account}
                            footer={
                                <Switch
                                    isChecked={accounts.some(
                                        (routingConfigAccount) =>
                                            routingConfigAccount.accountId ===
                                            account.bankAccountId,
                                    )}
                                    onCheckedChange={(checked: boolean) => {
                                        let accounts = incomeSplit.accounts;
                                        if (checked) {
                                            accounts.push({
                                                id: uuidv4(),
                                                paycheckRoutingConfigId:
                                                    incomeSplit.id,
                                                accountId:
                                                    account.bankAccountId,
                                            });
                                        } else {
                                            accounts = accounts.filter(
                                                (a) =>
                                                    a.accountId !==
                                                    account.bankAccountId,
                                            );
                                        }
                                        track(
                                            AnalyticsUserEvent.INCOME_SPLIT_ACCOUNTS_UPDATED,
                                            {
                                                accountId:
                                                    account.bankAccountId,
                                                action: checked
                                                    ? 'added'
                                                    : 'removed',
                                            },
                                        );
                                        captureMessage(
                                            `Income Split accounts updated: ${checked ? 'added' : 'removed'} account ${account.bankAccountId}`,
                                        );
                                        setIncomeSplit({
                                            ...incomeSplit,
                                            accounts,
                                        });
                                    }}
                                    style={{
                                        transformOrigin: 'right',
                                    }}
                                />
                            }
                        />
                    ))}
            </YStack>
        </YStack>
    );
};

const IncomeTypeOption = ({
    header,
    text,
    selected,
    onSelected,
}: {
    header: string;
    text: string;
    selected: boolean;
    onSelected: () => void;
}) => {
    return (
        <XStack
            paddingVertical={12}
            paddingHorizontal={16}
            gap={16}
            alignItems="center"
            justifyContent="space-between"
            onPress={() => {
                !selected && onSelected();
            }}
        >
            <YStack flexShrink={1}>
                <BodySmallEmphasized>{header}</BodySmallEmphasized>
                <BodySmallRegular color="$gray1000">{text}</BodySmallRegular>
            </YStack>
            <RadioInput
                isSelected={selected}
                onPress={() => {
                    !selected && onSelected();
                }}
            />
        </XStack>
    );
};

const IncomeType = ({ incomeSplit }: { incomeSplit: IncomeSplitConfig }) => {
    const setIncomeSplit = useSetAtom(incomeSplitAtom);

    return (
        <YStack gap={12}>
            <YStack>
                <BodyLargeEmphasized>Income type</BodyLargeEmphasized>
                <BodySmallRegular color="$gray1000">
                    Apply rules to all income or only to paychecks
                </BodySmallRegular>
            </YStack>
            <YStack
                backgroundColor="$gray200"
                borderRadius={16}
                separator={<Separator borderColor="$gray500" />}
            >
                <IncomeTypeOption
                    header="All income"
                    text="Any deposits, such as paychecks, interest earned, or cash back. Excludes ACH returns and reversals, provisional credits, and transfers initiated from Fruitful."
                    selected={!incomeSplit.onlyExecuteOnPaychecks}
                    onSelected={() => {
                        track(
                            AnalyticsUserEvent.INCOME_SPLIT_INCOME_TYPE_CHANGED,
                            {
                                incomeType: 'All income',
                            },
                        );
                        captureMessage(
                            'Income Split income type changed to: All income',
                        );
                        setIncomeSplit({
                            ...incomeSplit,
                            onlyExecuteOnPaychecks: false,
                        });
                    }}
                />
                <IncomeTypeOption
                    header="Paychecks only"
                    text="Any deposits that look like paychecks (we do our best to identify paychecks based on available transaction details)"
                    selected={incomeSplit.onlyExecuteOnPaychecks}
                    onSelected={() => {
                        track(
                            AnalyticsUserEvent.INCOME_SPLIT_INCOME_TYPE_CHANGED,
                            {
                                incomeType: 'Paychecks only',
                            },
                        );
                        captureMessage(
                            'Income Split income type changed to: Paychecks only',
                        );
                        setIncomeSplit({
                            ...incomeSplit,
                            onlyExecuteOnPaychecks: true,
                        });
                    }}
                />
            </YStack>
        </YStack>
    );
};

const IncomeSplitSettingsNew = () => {
    const incomeSplit = useAtomValue(incomeSplitAtom);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    if (!shouldSeeIncomeSplit || incomeSplit == null)
        return <Redirect href="/" />;

    return (
        <ScreenContainer
            title="Income settings"
            hasBackButton={true}
            onBack={() => {
                router.navigate('/income-split');
            }}
        >
            <YStack gap={24}>
                <IncomeType incomeSplit={incomeSplit} />
                <Accounts incomeSplit={incomeSplit} />
            </YStack>
        </ScreenContainer>
    );
};

export default IncomeSplitSettingsNew;
